"use client";
import clsx from "clsx";
import Preview from "components/blog-preview";
import Footer from "components/landing/footer";
import Pagination from "components/pagination";
import { useReCaptcha } from "next-recaptcha-v3";
import { Ultra } from "next/font/google";
import Image from "next/image";
import Link from "next/link";
import { useEffect, useState, Suspense } from "react";
import { Skeleton } from "components/ui/skeleton";
import { useSearchParams } from "next/navigation";

const ultra = Ultra({
  weight: "400", // Ultra is only available in 400 weight
  subsets: ["latin"],
});

// Skeleton components for loading states
const FeaturedArticleSkeleton = () => (
  <div className="mx-2 md:w-1/3 md:max-w-1/3 md:mx-auto space-y-8">
    <div className="text-center">
      <Skeleton className="h-10 w-4/5 mx-auto mb-2" />
    </div>
    <Skeleton className="h-96 w-full rounded-xl" />
    <div className="space-y-2">
      <Skeleton className="h-4 w-full" />
      <Skeleton className="h-4 w-full" />
      <Skeleton className="h-4 w-3/4" />
      <Skeleton className="h-4 w-1/3 mt-2" />
    </div>
  </div>
);

const ArticlePreviewSkeleton = () => (
  <div className="space-y-3">
    <Skeleton className="h-40 w-full rounded-lg" />
    <Skeleton className="h-5 w-3/4" />
    <Skeleton className="h-4 w-full" />
    <Skeleton className="h-4 w-full" />
    <Skeleton className="h-4 w-2/3" />
  </div>
);

// Component that uses searchParams
const BlogContent = () => {
  const searchParams = useSearchParams();
  const [articles, setArticles] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  
  // Extract page and size from URL params
  const page = Number(searchParams.get("page") || "1");
  const size = Number(searchParams.get("size") || "10");
  
  // Import 'executeRecaptcha' using 'useReCaptcha' hook
  const { executeRecaptcha, loaded } = useReCaptcha();

  useEffect(() => {
    const fetchArticles = async () => {
      setIsLoading(true);
      
      try {
        let token = await executeRecaptcha("page_load");

        const response = await fetch(
          `${process.env.NEXT_PUBLIC_API_ORIGIN}/blog/public?page=${page}&size=${size}`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              token
            },
          }
        );

        const result = await response.json();
        setArticles(result);

        token = await executeRecaptcha("page_load");

        const countResponse = await fetch(
          `${process.env.NEXT_PUBLIC_API_ORIGIN}/blog/public/count`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              token
            },
          }
        );

        const countResult = await countResponse.json();
        setTotalCount(countResult.count);
      } catch (error) {
        console.error("Error fetching blog posts:", error);
      } finally {
        setIsLoading(false);
      }
    };

    if (executeRecaptcha && loaded) {
      fetchArticles();
    }
  }, [executeRecaptcha, loaded, page, size]);

  return (
    <>
      {isLoading ? (
        <>
          <FeaturedArticleSkeleton />
          <div className="grid grid-cols-1 gap-8 mt-8 md:mt-16 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
            {Array.from({ length: 6 }).map((_, index) => (
              <ArticlePreviewSkeleton key={index} />
            ))}
          </div>
        </>
      ) : articles.length === 0 ? (
        <div className="text-center py-10">
          <h2 className={clsx("text-2xl", ultra.className)}>
            Aucun article disponible pour le moment.
          </h2>
        </div>
      ) : (
        <>
          {articles[0] && (
            <Link href={`/blog/${articles[0].slug}`}>
              <div className="mx-2 md:w-1/3 md:max-w-1/3 md:mx-auto space-y-8">
                <h1
                  className={clsx(
                    "text-4xl my-2 text-center underline",
                    ultra.className
                  )}
                >
                  {articles[0].title}
                </h1>

                <div className="h-96 overflow-hidden relative rounded-xl w-full">
                  <Image
                    src={articles[0].featuredImage}
                    alt={articles[0].title}
                    objectFit="cover"
                    fill
                    placeholder="blur"
                    blurDataURL={articles[0].blurredFeaturedImage}
                  />
                </div>

                <div>
                  <p className="whitespace-pre-wrap">{articles[0].excerpt}</p>
                  <p className="underline text-xs italic text-muted-foreground">
                    {"Lire la suite"}
                  </p>
                </div>
              </div>
            </Link>
          )}

          <div className="grid grid-cols-1 gap-8 mt-8 md:mt-16 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
            {articles
              .filter((e, idx) => idx > 0)
              .map((post: any) => (
                <Preview key={post.slug} {...post} />
              ))}
          </div>
        </>
      )}

      {(!isLoading && totalCount > 0) && (
        <Pagination count={totalCount} size={size} page={page} />
      )}
    </>
  );
};

const Page = () => {
  useEffect(() => {
    if ("$crisp" in window) {
      (window.$crisp as { push: (arg: Array<string>) => void }).push([
        "do",
        "chat:hide",
      ]);
    }
  }, []);

  return (
    <>
      <header className="flex justify-between sticky top-0 z-10 glassmorphic-overlay bg-background/30 backdrop-blur-md h-[3.5rem]">
        <meta property="og:title" content="Le blog" />
        <title>{"Le blog"}</title>
        <meta name="description" content="Blog | Jennyz" />
        <meta
          name="keywords"
          content="gestion de restaurant, logiciel de caisse, suivi des stocks, programme de fidélité, conformité hygiène, indicateurs de rentabilité, audit de caisse"
        />
        <meta
          property="og:description"
          content="Blog | Jennyz : l'actualité restauration"
        />
        <meta property="og:image" content="https://jennyz.co/jennys.png" />
        <Link href="/">
          <Image
            src="/logo-light.png"
            alt={"jennyz logo"}
            fill
            objectFit="cover"
            className="dark:hidden block max-w-40 w-32"
          />
          <Image
            src="/logo-dark.png"
            alt={"jennyz logo"}
            fill
            objectFit="cover"
            className="hidden dark:block max-w-40 w-32"
          />
        </Link>
      </header>

      <main className="space-y-14 mt-6 xl:mt-14 px-2 md:px-4 min-h-[100vh]">
        <Suspense fallback={
          <>
            <FeaturedArticleSkeleton />
            <div className="grid grid-cols-1 gap-8 mt-8 md:mt-16 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
              {Array.from({ length: 6 }).map((_, index) => (
                <ArticlePreviewSkeleton key={index} />
              ))}
            </div>
          </>
        }>
          <BlogContent />
        </Suspense>
      </main>

      <Footer />
    </>
  );
};

export default Page;
